export enum SessionHitResult {
  Single = 'single',
  Double = 'double',
  Triple = 'triple',
  HomeRun = 'home-run',
  FlyOut = 'fly-out',
  GroundOut = 'ground-out',
  Foul = 'foul',
  FoulOut = 'foul-out',
  SwingingStrike = 'swinging-strike',
  CalledStrike = 'called-strike',
  Ball = 'ball',
}

/** legend, filter, and sort order */
export const SESSION_HIT_RESULTS: SessionHitResult[] = [
  SessionHitResult.Single,
  SessionHitResult.Double,
  SessionHitResult.Triple,
  SessionHitResult.HomeRun,
  SessionHitResult.FlyOut,
  SessionHitResult.GroundOut,
  SessionHitResult.Foul,
  SessionHitResult.FoulOut,
  SessionHitResult.SwingingStrike,
  SessionHitResult.CalledStrike,
  SessionHitResult.Ball,
];

export const SESSION_HIT_RESULT_LABELS: Record<SessionHitResult, string> = {
  [SessionHitResult.Single]: 'hitters.recap-result-single',
  [SessionHitResult.Double]: 'hitters.recap-result-double',
  [SessionHitResult.Triple]: 'hitters.recap-result-triple',
  [SessionHitResult.HomeRun]: 'hitters.recap-result-home-run',
  [SessionHitResult.FlyOut]: 'hitters.recap-result-fly-out',
  [SessionHitResult.GroundOut]: 'hitters.recap-result-ground-out',
  [SessionHitResult.Foul]: 'hitters.recap-result-foul',
  [SessionHitResult.FoulOut]: 'hitters.recap-result-foul-out',
  [SessionHitResult.SwingingStrike]: 'hitters.recap-result-swinging-strike',
  [SessionHitResult.CalledStrike]: 'hitters.recap-result-called-strike',
  [SessionHitResult.Ball]: 'hitters.recap-result-ball',
};

/** Statcast attack zones */
export enum PitchLocation {
  Heart = 'heart',
  Shadow = 'shadow',
  Chase = 'chase',
  Waste = 'waste',
}

/** Statcast launch_speed_angle buckets */
export enum ContactQuality {
  Weak = 'weak',
  Topped = 'topped',
  Under = 'under',
  FlareBurner = 'flare-burner',
  Solid = 'solid',
  Barrel = 'barrel',
}

export const CONTACT_QUALITY_LABELS: Record<ContactQuality, string> = {
  [ContactQuality.Weak]: 'hitters.recap-contact-weak',
  [ContactQuality.Topped]: 'hitters.recap-contact-topped',
  [ContactQuality.Under]: 'hitters.recap-contact-under',
  [ContactQuality.FlareBurner]: 'hitters.recap-contact-flare-burner',
  [ContactQuality.Solid]: 'hitters.recap-contact-solid',
  [ContactQuality.Barrel]: 'hitters.recap-contact-barrel',
};

export enum BattedBallType {
  GroundBall = 'ground-ball',
  LineDrive = 'line-drive',
  FlyBall = 'fly-ball',
  Popup = 'popup',
}

export const BATTERED_BALL_TYPE_LABELS: Record<BattedBallType, string> = {
  [BattedBallType.GroundBall]: 'hitters.recap-batted-ground-ball',
  [BattedBallType.LineDrive]: 'hitters.recap-batted-line-drive',
  [BattedBallType.FlyBall]: 'hitters.recap-batted-fly-ball',
  [BattedBallType.Popup]: 'hitters.recap-batted-popup',
};

export enum SwingDecision {
  Swing = 'swing',
  Take = 'take',
}

export enum SprayDirection {
  Pull = 'pull',
  Center = 'center',
  Oppo = 'oppo',
}

export enum SprayDepth {
  Infield = 'infield',
  Outfield = 'outfield',
}

export enum ZoneMetricFamily {
  Pitches = 'pitches',
  Swing = 'swing',
  Contact = 'contact',
  BattedBall = 'batted-ball',
  Quality = 'quality',
  Trajectory = 'trajectory',
  Production = 'production',
}

export enum ZoneMetric {
  PitchRate = 'pitch-rate',
  Pitches = 'pitches',
  Swing = 'swing',
  Swings = 'swings',
  Chase = 'chase',
  Contact = 'contact',
  Contacts = 'contacts',
  Whiff = 'whiff',
  Whiffs = 'whiffs',
  BattedBalls = 'batted-balls',
  ExitVelo = 'exit-velo',
  MaxExitVelo = 'max-exit-velo',
  LaunchAngle = 'launch-angle',
  SweetSpot = 'sweet-spot',
  Barrels = 'barrels',
  BarrelRate = 'barrel-rate',
  HardHits = 'hard-hits',
  HardHit = 'hard-hit',
  GroundBall = 'ground-ball',
  LineDrive = 'line-drive',
  FlyBall = 'fly-ball',
  Popup = 'popup',
  Bacon = 'bacon', // batting average on contact
  XBacon = 'xbacon', // expected batting average on contact
  XWobacon = 'xwobacon', // expected weighted on-base average on contact
}

export enum RecapColorBy {
  Result = 'result',
  PitchType = 'pitch-type',
  Contact = 'contact',
  ExitVelo = 'exit-velo',
  LaunchAngle = 'launch-angle',
}

export enum RecapFieldMode {
  Spray = 'spray',
  Zones = 'zones',
}

export enum RecapZoneOverlay {
  None = 'none',
  Zones = 'zones',
  Heatmap = 'heatmap',
}

export enum RecapZoneFilterKind {
  Plate = 'plate',
  Spray = 'spray',
}

export type ZoneAggregate = 'mean' | 'count' | 'max' | 'share';

export const HARD_HIT_MPH = 95;

/** distance at which a batted ball is considered to have reached the outfield */
export const INFIELD_DEPTH_FT = 150;

/** spray angle (deg) separating pull/oppo from straightaway */
export const SPRAY_CENTER_DEG = 15;

export const EXIT_VELO_SCALE = { min: 70, baseline: 88.5, max: 105 };

/** Statcast sweet-spot launch angles, inclusive */
export const SWEET_SPOT_LA = { min: 8, max: 32 };
